import React, { useMemo, useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import dayjs from 'dayjs';
import { ProCard, ProTablePlus } from '@bedrock/eed-react';
import { Button, Modal } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import DeviceModal from './DeviceModal';
import type { DeviceManagementProps, DeviceRecord } from './types';

const EMPTY_PLACEHOLDER = '--';
const PAGE_SIZE = 20;
const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const DeviceManagement: React.FC<DeviceManagementProps> = (props) => {
  const { vendorId, fetchList, addDevice, updateDevice, deleteDevice, tenant } = props;
  console.log(props,'fetchList')
  const tableRef = useRef<{ query: () => void }>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DeviceRecord | null>(null);

  const queryService = useMemoizedFn(async (query) => {
    const { current, pageSize, search_text } = query?.params;
    return  fetchList?.({
      vendorId: vendorId,
      current: current,
      limit: pageSize,
      key: search_text,
      tenant: tenant,
    }).then((result)=>{
      console.log(result,'result')
      return {
        data: {
          list: result?.data?.records || [],
          total: result?.data?.total,
        },
      };
    })
  });

  const refreshTable = useMemoizedFn(() => {
    tableRef.current?.query?.();
  });

  const handleAdd = useMemoizedFn(() => {
    setEditingRecord(null);
    setModalVisible(true);
  });

  const handleEdit = useMemoizedFn((record: DeviceRecord) => {
    setEditingRecord(record);
    setModalVisible(true);
  });

  const handleModalCancel = useMemoizedFn(() => {
    setModalVisible(false);
    setEditingRecord(null);
  });

  const handleModalOk = useMemoizedFn(
    async (values: { mac_address: string; remark?: string }) => {
      if (editingRecord) {
        await updateDevice({ ...values, deviceId: editingRecord.id, vendorId: vendorId });
      } else {
        await addDevice({ ...values, vendorId: vendorId });
      }
      refreshTable();
    },
  );

  const handleDelete = useMemoizedFn((record: DeviceRecord) => {
    Modal.confirm({
      title: '确定删除此设备吗？',
      iconClassName: 'error',
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteDevice({ deviceId: record.id, vendorId: vendorId });
        refreshTable();
      },
    });
  });

  const columns = useMemo(
    () => [
      {
        title: 'Mac地址',
        dataIndex: 'macAddress',
        width: 320,
        fixed: 'left' as const,
        render: (value: string) => (
          <span className="font-bold">{value || EMPTY_PLACEHOLDER}</span>
        ),
      },
      {
        title: '备注',
        dataIndex: 'remark',
        ellipsis: true,
        render: (value: string) => value || EMPTY_PLACEHOLDER,
      },
      {
        title: '创建人',
        dataIndex: ['creator', 'name'],
        width: 120,
        render: (value: string) => value || EMPTY_PLACEHOLDER,
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        width: 180,
        render: (value: string) =>
          value ? dayjs(value).format(DATE_FORMAT) : EMPTY_PLACEHOLDER,
      },
      {
        title: '操作',
        dataIndex: 'operation',
        fixed: 'right' as const,
        width: 104,
        render: (_: unknown, record: DeviceRecord) => (
          <div className="flex gap-3">
            <Button type="text" size="small" onClick={() => handleEdit(record)}>
              编辑
            </Button>
            <Button
              type="text"
              danger
              size="small"
              onClick={() => handleDelete(record)}
            >
              删除
            </Button>
          </div>
        ),
      },
    ],
    [handleEdit, handleDelete],
  );

  return (
    <>
      <ProCard title="设备信息">
        <ProTablePlus
          rowKey="id"
          ref={tableRef}
          columns={columns}
          request={{ queryService }}
          pagination={{ pageSize: PAGE_SIZE }}
          toolbar={{
            actionsInTitle: true,
            search: {
              placeholder: '请输入Mac地址/备注',
              id: 'search_text',
              searchOnBlur: true,
            },
            filterSetting: {
              disableAddButton: true,
            },
            settings: [
              <Button
                key="add"
                type="text-subtle-primary"
                size="small"
                icon={<Plus />}
                onClick={handleAdd}
              >
                添加设备
              </Button>,
            ],
          }}
        />
      </ProCard>
      <DeviceModal
        visible={modalVisible}
        editingRecord={editingRecord}
        onCancel={handleModalCancel}
        onOk={handleModalOk}
      />
    </>
  );
};

export default DeviceManagement;